import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from '../lib/i18n';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.15
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} style={{
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(24px)',
    transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms`
  }} data-id="element-650" data-mp="3icvkmthqqaj1">
      {children}
    </div>;
};
const Counter = ({
  end,
  duration = 1500,
  suffix = ''
}: {
  end: number;
  duration?: number;
  suffix?: string;
}) => {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) {
        setStarted(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.4
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    let frameId: number;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * end));
      if (progress < 1) frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [started, end, duration]);
  return <span ref={ref} data-id="element-651" data-mp="3w3otxlyrd8ni">
      {value}
      {suffix}
    </span>;
};
export function AboutPage() {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const stats = [{
    value: 250,
    suffix: en ? 'kg' : ru ? 'кг' : 'kg',
    label: en ? 'Max capacity' : ru ? 'Макс. грузоподъемность' : 'Maks. nosivost'
  }, {
    text: '4–8',
    label: en ? 'Weeks delivery' : ru ? 'Недель доставка' : 'Nedelja isporuka'
  }, {
    value: 1,
    suffix: '',
    label: en ? 'Year warranty' : ru ? 'Год гарантии' : 'Godina garancije'
  }, {
    value: 4,
    suffix: '',
    label: en ? 'Models available' : ru ? 'Доступных моделей' : 'Dostupna modela'
  }];
  return <main className="min-h-screen bg-dark-bg py-24" data-id="element-652" data-mp="2pidxojp81sl1">
      <div className="max-w-[700px] mx-auto px-5 sm:px-6" data-id="element-653" data-mp="14zaf6b73kfb8">
        <FadeIn data-id="element-654" data-mp="14yqwgs9mhmw5">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight" data-id="element-655" data-mp="151d2uoks1wcs">
            {en ? 'About us: ' : ru ? 'О нас: ' : 'O nama: '}
            <span className="text-brand-yellow" data-id="element-656" data-mp="1dzp19crvxyfs">D-TECH</span>
          </h1>
          <div className="w-16 h-1 bg-brand-yellow rounded-full mb-12" data-id="element-657" data-mp="1iz1qu5a55jm2" />
        </FadeIn>

        <FadeIn delay={50} data-id="element-658" data-mp="lbb38x0sr248">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16" data-id="element-659" data-mp="lc2njboy1df7">
            {stats.map((stat, i) => <div key={i} className="bg-dark-surface border border-dark-border rounded-xl p-4 text-center hover:border-brand-yellow/60 transition-colors duration-300" data-id="element-660" data-mp="qbfk4o5m9yuz" data-mpi={i}>
                <div className="text-3xl md:text-4xl font-black text-brand-yellow leading-none mb-2" data-id="element-661" data-mp="101s4af9irq0l">
                  {'text' in stat ? stat.text : <Counter end={stat.value} suffix={stat.suffix} data-id="element-662" data-mp="7ibowycr3dy9" />}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wide" data-id="element-663" data-mp="299bui98topoc">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </FadeIn>

        <FadeIn delay={80} data-id="element-664" data-mp="2j0gbtflz0mmc">
          <div className="space-y-6 text-lg text-gray-300 leading-[1.8]" data-id="element-665" data-mp="2j0pxws3wa9mr">
            {en ? <>
                <p data-id="element-666" data-mp="2swt203ib4icc">
                  D-TECH specializes in the import and sale of professional
                  vertical cargo transport equipment, with a focus on safety,
                  reliability and ease of use.
                </p>
                <p data-id="element-667" data-mp="31wg12inkdc28">
                  Our goal is to enable companies and individuals to handle
                  cargo more efficiently while reducing physical strain,
                  increasing safety and improving everyday work processes.
                </p>
                <p data-id="element-668" data-mp="12l5844tojp7z">
                  We work directly with manufacturers to ensure proven
                  equipment, technical support and spare parts. Due to increased
                  demand, the availability of certain models may change on a
                  daily basis, while the delivery time for models not currently
                  in stock ranges from 4 to 8 weeks, depending on the model and
                  the quantity ordered.
                </p>
                <p data-id="element-669" data-mp="39ew2x8paeh83">
                  We provide technical support, servicing and access to genuine
                  spare parts to ensure safe, long-term operation of the
                  equipment for our customers.
                </p>
              </> : ru ? <>
                <p data-id="element-670" data-mp="2mj5lpymfl5m3">
                  D-TECH занимается импортом и продажей профессионального
                  оборудования для вертикальной транспортировки грузов, уделяя
                  особое внимание безопасности, надёжности и простоте
                  использования.
                </p>
                <p data-id="element-671" data-mp="oox71xlinylu">
                  Наша цель — позволить компаниям и частным лицам эффективнее
                  обращаться с грузами, снижая физическую нагрузку, повышая
                  безопасность и улучшая повседневные рабочие процессы.
                </p>
                <p data-id="element-672" data-mp="1oo305kmf6gbg">
                  Мы работаем напрямую с производителями, чтобы обеспечить
                  проверенное оборудование, техническую поддержку и запасные
                  части. Из-за повышенного спроса наличие отдельных моделей
                  может меняться ежедневно, а срок поставки моделей, которых
                  сейчас нет в наличии, составляет от 4 до 8 недель в
                  зависимости от модели и заказанного количества.
                </p>
                <p data-id="element-673" data-mp="22nuhnvc5aeu7">
                  Мы предоставляем техническую поддержку, сервис и доступ к
                  оригинальным запасным частям, чтобы обеспечить безопасную и
                  долговременную работу оборудования для наших клиентов.
                </p>
              </> : <>
                <p data-id="element-674" data-mp="32o6cizyh7jfm">
                  D-TECH se bavi uvozom i prodajom profesionalne opreme za
                  vertikalni transport tereta, sa fokusom na bezbednost,
                  pouzdanost i jednostavnu upotrebu.
                </p>
                <p data-id="element-675" data-mp="3gmnz2ucnapvy">
                  Naš cilj je da kompanijama i pojedincima omogućimo efikasnije
                  rukovanje teretom uz smanjenje fizičkog opterećenja, povećanje
                  bezbednosti i unapređenje svakodnevnih radnih procesa.
                </p>
                <p data-id="element-676" data-mp="3qizg1xn6k3wq">
                  Sarađujemo direktno sa proizvođačima kako bismo obezbedili
                  proverenu opremu, tehničku podršku i rezervne delove. Zbog
                  povećane potražnje, dostupnost pojedinih modela može se
                  menjati na dnevnom nivou, dok rok isporuke za modele koji
                  trenutno nisu na stanju iznosi od 4 do 8 nedelja, u zavisnosti
                  od modela i naručene količine.
                </p>
                <p data-id="element-677" data-mp="yfktb5pjdqyt">
                  Pružamo tehničku podršku, servis i pristup originalnim
                  rezervnim delovima kako bismo korisnicima obezbedili siguran i
                  dugoročan rad opreme.
                </p>
              </>}
          </div>
        </FadeIn>
      </div>
    </main>;
}